<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <!-- Header with controls -->
        <div class="d-flex justify-space-between align-center mb-6 flex-wrap ga-3">
          <h1 class="text-primary weight-600">API Monitoring</h1>
          <div class="d-flex align-center ga-3 flex-wrap">
            <v-select
              v-model="selectedHours"
              :items="timeRangeOptions"
              label="Time Range"
              density="comfortable"
              variant="outlined"
              hide-details
              style="min-width: 150px"
              @update:model-value="loadMetrics" />
            <v-btn color="primary" :disabled="loading" @click="loadMetrics">
              {{ loading ? "Refreshing..." : "Refresh" }}
            </v-btn>
            <span v-if="lastUpdated" class="text-body-2 text-color-blue-gray">
              Last updated: {{ formatTime(lastUpdated) }}
            </span>
          </div>
        </div>

        <!-- Error alert -->
        <v-alert v-if="error" type="error" variant="outlined" closable class="mb-6" @click:close="error = null">
          {{ error }}
        </v-alert>

        <!-- Loading state -->
        <div v-if="loading && !metrics">
          <v-skeleton-loader type="card" class="mb-4" />
          <v-skeleton-loader type="card" class="mb-4" />
          <v-skeleton-loader type="card" />
        </div>

        <!-- Metrics content -->
        <v-row v-if="!loading || metrics">
          <!-- Health Checks Card -->
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title class="text-h5 text-primary weight-600"> Health Checks </v-card-title>
              <v-card-text v-if="metrics">
                <v-row>
                  <v-col cols="6" sm="3">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">SUCCESS RATE</div>
                    <div class="text-h5 weight-600" :class="getUptimeClass(metrics.healthChecks.uptimePercentage)">
                      {{ metrics.healthChecks.uptimePercentage }}%
                    </div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">TOTAL CHECKS</div>
                    <div class="text-h5 weight-600 text-primary">
                      {{ metrics.healthChecks.total }}
                    </div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">SUCCESSFUL</div>
                    <div class="text-h5 weight-600 color-success">
                      {{ metrics.healthChecks.successful }}
                    </div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">AVG RESPONSE</div>
                    <div class="text-h5 weight-600 text-primary">{{ metrics.healthChecks.avgResponseTimeMs }} ms</div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Prior Auth Requests Card -->
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title class="text-h5 text-primary weight-600"> Prior Authorization Requests </v-card-title>
              <v-card-text v-if="metrics">
                <v-row>
                  <v-col cols="6" sm="4">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">TOTAL</div>
                    <div class="text-h5 weight-600 text-primary">
                      {{ metrics.priorAuthRequests.total }}
                    </div>
                  </v-col>
                  <v-col cols="6" sm="4">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">SUCCESS RATE</div>
                    <div class="text-h5 weight-600" :class="getSuccessRateClass(metrics.priorAuthRequests.successRate)">
                      {{ metrics.priorAuthRequests.successRate }}%
                    </div>
                  </v-col>
                  <v-col cols="6" sm="4">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">FAILED</div>
                    <div class="text-h5 weight-600 color-error">
                      {{ metrics.priorAuthRequests.failed }}
                    </div>
                  </v-col>
                  <v-col cols="6" sm="6">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">AVG RESPONSE</div>
                    <div class="text-h5 weight-600 text-primary">
                      {{ metrics.priorAuthRequests.avgResponseTimeMs }} ms
                    </div>
                  </v-col>
                  <v-col cols="6" sm="6">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">THROUGHPUT</div>
                    <div class="text-h5 weight-600 text-primary">
                      {{ metrics.priorAuthRequests.throughputPerHour }} req/hr
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Status Distribution Card -->
          <v-col cols="12">
            <v-card>
              <v-card-title class="text-h5 text-primary weight-600"> Response Status Distribution </v-card-title>
              <v-card-text v-if="metrics">
                <div v-if="metrics.priorAuthRequests.statusDistribution.length > 0" class="d-flex flex-wrap ga-3">
                  <StatusLabel
                    v-for="status in metrics.priorAuthRequests.statusDistribution"
                    :key="status.responseStatusCode"
                    :text="`${status.responseStatusCode} (${status.count})`"
                    :color="getStatusLabelColor(status.responseStatusCode)"
                    :dot="true" />
                </div>
                <div v-else class="text-center text-body-2 text-color-blue-gray py-8">
                  No requests in selected time range
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Outages Card -->
          <v-col v-if="metrics && metrics.outages" cols="12">
            <v-card>
              <v-card-title class="text-h5 text-primary weight-600"> System Outages </v-card-title>
              <v-card-text>
                <v-row class="mb-4">
                  <v-col cols="12" sm="4" :md="metrics.outages.ongoingOutage ? 3 : 4">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">TOTAL OUTAGES</div>
                    <div class="text-h5 weight-600 text-primary">
                      {{ metrics.outages.totalCount }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="4" :md="metrics.outages.ongoingOutage ? 3 : 4">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">TOTAL DOWNTIME</div>
                    <div class="text-h5 weight-600 text-primary">
                      {{ formatDuration(metrics.outages.totalDowntimeSeconds) }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="4" :md="metrics.outages.ongoingOutage ? 3 : 4">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">UPTIME</div>
                    <div class="text-h5 weight-600" :class="getUptimeColorClass(metrics.outages.uptimePercentage)">
                      {{ metrics.outages.uptimePercentage }}%
                    </div>
                  </v-col>
                  <v-col v-if="metrics.outages.ongoingOutage" cols="12" sm="12" md="3">
                    <div class="text-body-3-1 text-color-blue-gray weight-600 mb-2">ONGOING OUTAGE</div>
                    <div class="text-h5 weight-600 color-error">
                      {{ formatTime(new Date(metrics.outages.ongoingOutage.startedAt)) }}
                    </div>
                  </v-col>
                </v-row>

                <div v-if="metrics.outages.recentOutages && metrics.outages.recentOutages.length > 0">
                  <v-data-table :headers="outageHeaders" :items="metrics.outages.recentOutages" :items-per-page="10">
                    <template #item.startedAt="{ item }">
                      {{ formatTimestamp(item.startedAt) }}
                    </template>
                    <template #item.endedAt="{ item }">
                      {{ item.endedAt ? formatTimestamp(item.endedAt) : "-" }}
                    </template>
                    <template #item.durationSeconds="{ item }">
                      {{ item.durationSeconds ? formatDuration(item.durationSeconds) : "Ongoing" }}
                    </template>
                    <template #item.status="{ item }">
                      <StatusLabel
                        :text="item.isOngoing ? 'Ongoing' : 'Resolved'"
                        :color="item.isOngoing ? 'error' : 'success'"
                        :dot="true" />
                    </template>
                  </v-data-table>
                </div>
                <div v-else class="text-center text-body-2 text-color-blue-gray py-8">
                  No outages detected in selected time range
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Recent Requests Card -->
          <v-col cols="12">
            <v-card>
              <v-card-title class="text-h5 text-primary weight-600"> Recent Requests </v-card-title>
              <v-card-text v-if="metrics">
                <div v-if="metrics.priorAuthRequests.recentRequests.length > 0">
                  <v-data-table
                    :headers="requestHeaders"
                    :items="metrics.priorAuthRequests.recentRequests"
                    :items-per-page="10">
                    <template #item.createdOn="{ item }">
                      {{ formatTimestamp(item.createdOn) }}
                    </template>
                    <template #item.messageId="{ item }">
                      <span class="monospace-text">{{ item.messageId }}</span>
                    </template>
                    <template #item.patientLastName="{ item }">
                      {{ item.patientLastName || "N/A" }}
                    </template>
                    <template #item.ndc="{ item }">
                      <span class="monospace-text">{{ item.ndc || "N/A" }}</span>
                    </template>
                    <template #item.responseStatusCode="{ item }">
                      <StatusLabel
                        :text="String(item.responseStatusCode)"
                        :color="getStatusLabelColor(item.responseStatusCode)"
                        :dot="true" />
                    </template>
                    <template #item.responseTimeMs="{ item }"> {{ item.responseTimeMs }} ms </template>
                  </v-data-table>
                </div>
                <div v-else class="text-center text-body-2 text-color-blue-gray py-8">
                  No recent requests in selected time range
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import StatusLabel from "@/components/StatusLabel.vue";
import { monitoringServices } from "@/services/monitoringService";

const metrics = ref(null);
const loading = ref(true);
const error = ref(null);
const selectedHours = ref(24);
const lastUpdated = ref(null);
let refreshInterval = null;

const timeRangeOptions = [
  { title: "Last Hour", value: 1 },
  { title: "Last 6 Hours", value: 6 },
  { title: "Last 24 Hours", value: 24 },
  { title: "Last 3 Days", value: 72 },
  { title: "Last Week", value: 168 },
];

const outageHeaders = [
  { title: "Started At", value: "startedAt", sortable: true },
  { title: "Ended At", value: "endedAt", sortable: true },
  { title: "Duration", value: "durationSeconds", sortable: true },
  { title: "Status", value: "status", sortable: false },
];

const requestHeaders = [
  { title: "Timestamp", value: "createdOn", sortable: true },
  { title: "Message ID", value: "messageId", sortable: false },
  { title: "Patient", value: "patientLastName", sortable: true },
  { title: "NDC", value: "ndc", sortable: false },
  { title: "Status", value: "responseStatusCode", sortable: true },
  { title: "Response Time", value: "responseTimeMs", sortable: true },
];

const loadMetrics = async () => {
  loading.value = true;
  error.value = null;

  try {
    metrics.value = await monitoringServices.getMetrics(selectedHours.value);
    lastUpdated.value = new Date();
  } catch (err) {
    error.value = `Failed to load metrics: ${err.message}`;
  } finally {
    loading.value = false;
  }
};

const formatTime = (date) => {
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const formatDuration = (seconds) => {
  if (!seconds) return "0s";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(" ");
};

const getUptimeClass = (uptime) => {
  if (uptime >= 99) return "color-success";
  if (uptime >= 90) return "color-warning";
  return "color-error";
};

const getSuccessRateClass = (rate) => {
  if (rate >= 95) return "color-success";
  if (rate >= 80) return "color-warning";
  return "color-error";
};

const getUptimeColorClass = (uptime) => {
  if (uptime >= 99.9) return "color-success";
  if (uptime >= 99) return "color-warning";
  return "color-error";
};

const getStatusLabelColor = (statusCode) => {
  if (statusCode >= 200 && statusCode < 300) return "success";
  if (statusCode >= 400 && statusCode < 500) return "warning";
  if (statusCode >= 500) return "error";
  return "default";
};

onMounted(() => {
  loadMetrics();

  // Auto-refresh every 30 seconds
  refreshInterval = setInterval(loadMetrics, 30000);
});

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
});
</script>
